import moment from "moment";
import colors from "tailwindcss/colors";

import {
  USAGE_TYPE_KEYS,
  UsageTypeKey,
  ProcessedData,
  ProcessedUsageItem,
  ChartDataPoint,
  UsageLimits,
} from "./types";
import { strokes } from "./constants";

import { OrganizationUsageChartData, OrganizationUsagePeriod } from "@/types/organization-usage";

export const formatDatetime = (datetime: string, startDate: string, endDate: string): string => {
  const isOneDay = startDate === endDate;
  const sameYear = moment(startDate).year() === moment(endDate).year();

  const getDateFormat = (): string => {
    if (isOneDay) {
      return "HH:mm A";
    }

    if (sameYear) {
      return "MMM DD";
    }

    return "MMM DD, YYYY";
  };

  return moment(datetime).format(getDateFormat());
};

export const processUsageData = (
  data: OrganizationUsageChartData | undefined,
  startDate: string,
  endDate: string,
): ProcessedData => {
  if (!data?.workspaces) {
    return {
      evalCells: {},
      agents: {},
      playgroundRuns: {},
      requestLogs: {},
      traces: {},
      workspaceInfo: {},
    };
  }

  const workspaceInfo = data.workspaces.reduce(
    (acc, ws) => ({
      ...acc,
      [ws.workspace_id]: ws.workspace_name,
    }),
    {},
  );

  return Object.keys(USAGE_TYPE_KEYS).reduce(
    (acc, key) => ({
      ...acc,
      [key]: data.workspaces.reduce((wsAcc, ws) => {
        const allUsage = [
          ...ws.usage.map(u => ({ ...u, isProjected: false })),
          ...(ws.projected_usage || []).map(u => ({
            ...u,
            isProjected: true,
          })),
        ];

        return {
          ...wsAcc,
          [ws.workspace_id]: allUsage.map(u => {
            const usageKey = USAGE_TYPE_KEYS[key as UsageTypeKey];
            return {
              [key]: u[usageKey as keyof OrganizationUsagePeriod],
              rawDatetime: u.period_start,
              datetime: formatDatetime(u.period_start, startDate, endDate),
              isProjected: u.isProjected,
            };
          }),
        };
      }, {}),
    }),
    {
      evalCells: {},
      agents: {},
      playgroundRuns: {},
      requestLogs: {},
      traces: {},
      workspaceInfo,
    },
  );
};

export const calculateNumberOfPeriods = (
  workspaceData: Record<string, ProcessedUsageItem[]>,
): number => {
  const allDatetimes = new Set<string>();
  Object.values(workspaceData).forEach(wsData => {
    wsData.forEach(item => {
      allDatetimes.add(moment(item.rawDatetime).format("MMM"));
    });
  });
  return allDatetimes.size;
};

export const transformDataForChart = (
  usageType: UsageTypeKey,
  workspaceData: Record<string, ProcessedUsageItem[]>,
  workspaceInfo: Record<string, string>,
  chartType?: "cumulative" | "periodic",
): ChartDataPoint[] => {
  if (!workspaceData || Object.values(workspaceData).every(ws => ws.length === 0)) {
    return [];
  }

  const allDays = new Set<string>();
  const projectedDays = new Set<string>();

  Object.values(workspaceData).forEach(wsData => {
    wsData.forEach(item => {
      allDays.add(item.datetime);
      if (item.isProjected) {
        projectedDays.add(item.datetime);
      }
    });
  });

  const dayMap = new Map<string, Record<string, number | null>>();
  allDays.forEach(datetime => {
    dayMap.set(datetime, {});
  });

  if (chartType === "cumulative") {
    const cumulativeData: Record<string, number> = {};

    Object.entries(workspaceData).forEach(([workspaceId]) => {
      const workspaceName = workspaceInfo[workspaceId] || `Workspace ${workspaceId}`;
      cumulativeData[workspaceName] = 0;
    });

    const sortedDays = Array.from(allDays).sort((a, b) => {
      const aDate = new Date(a);
      const bDate = new Date(b);
      return aDate.getTime() - bDate.getTime();
    });

    sortedDays.forEach(datetime => {
      Object.entries(workspaceData).forEach(([workspaceId, wsData]) => {
        const workspaceName = workspaceInfo[workspaceId] || `Workspace ${workspaceId}`;

        const periodData = wsData.find(item => item.datetime === datetime);
        const value = periodData ? (periodData[usageType] as number) || 0 : 0;
        cumulativeData[workspaceName] += value;
        dayMap.get(datetime)![workspaceName] = cumulativeData[workspaceName];
      });
    });
  } else {
    // Initialize all workspaces with 0 for all datetimes
    allDays.forEach(datetime => {
      Object.entries(workspaceData).forEach(([workspaceId]) => {
        const workspaceName = workspaceInfo[workspaceId] || `Workspace ${workspaceId}`;
        dayMap.get(datetime)![workspaceName] = 0;
      });
    });

    // Now fill in actual values
    Object.entries(workspaceData).forEach(([workspaceId, wsData]) => {
      const workspaceName = workspaceInfo[workspaceId] || `Workspace ${workspaceId}`;

      wsData.forEach(item => {
        if (dayMap.has(item.datetime)) {
          const value = (item[usageType] as number) || 0;
          dayMap.get(item.datetime)![workspaceName] = value;
        }
      });
    });
  }

  return Array.from(dayMap.entries()).map(([datetime, workspaceValues]) => ({
    datetime,
    rawDatetime: datetime,
    isProjected: projectedDays.has(datetime),
    ...workspaceValues,
  }));
};

export const createChartSeries = (
  workspaceData: Record<string, ProcessedUsageItem[]>,
  workspaceInfo: Record<string, string>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any[] => {
  if (!workspaceData || Object.keys(workspaceData).length === 0) {
    return [];
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const series: any[] = [];
  Object.entries(workspaceData).forEach(([workspaceId], index) => {
    const workspaceName = workspaceInfo[workspaceId] || `Workspace ${workspaceId}`;

    series.push({
      type: "bar",
      xKey: "datetime",
      yKey: workspaceName,
      yName: workspaceName,
      stacked: true,
      strokeWidth: 0,
      marker: {
        enabled: false,
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      itemStyler: (params: any) => {
        if (params.datum.isProjected) {
          return {
            fillOpacity: 0.5,
          };
        }
        return undefined;
      },
      highlightStyle: {
        item: {
          strokeWidth: 2,
          stroke: strokes[index],
        },
      },
      tooltip: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        renderer: (params: any) => {
          const value = params.datum[params.yKey];
          const formattedValue =
            typeof value === "number"
              ? value.toLocaleString("en-US", {
                  maximumFractionDigits: 0,
                })
              : (value ?? 0);
          const { isProjected } = params.datum;

          return `
           <div style="display: flex; flex-direction: column; gap: 4px;">
              ${
                index === 0
                  ? `<div style="padding: 4px 8px; font-weight: 500; color: ${colors.gray[700]};">
                      ${params.datum.datetime}${
                        isProjected
                          ? ' <span style="color: ' +
                            colors.gray[500] +
                            '; font-size: 0.85em;">(Projected)</span>'
                          : ""
                      }
                    </div>`
                  : ""
              }
              <div style="padding: 0px 8px;">
                <div style="display: flex; justify-content: space-between; gap: 4px; margin-bottom: 4px;">
                  <div style="display: flex; align-items: center; gap: 4px;">
                    <div style="width: 10px; height: 10px; border-radius: 50%; background-color: ${
                      params.fill
                    };"></div>
                    <span style="font-weight: 500; color: ${
                      colors.gray[500]
                    };">${params.yName}:</span>
                  </div>
                  <div style="margin-left: 12px; font-weight: 500; color: ${
                    colors.gray[700]
                  };">${formattedValue}</div>
                </div>
              </div>
            </div>
          `;
        },
      },
    });
  });

  return series;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const createUsageLimits = (subscriptionData: any): UsageLimits => {
  const subscription = subscriptionData?.subscription;
  return {
    evaluations: subscription?.included_eval_cells || "Unlimited",
    agents: subscription?.included_workflow_nodes || "Unlimited",
    prompts: subscription?.included_requests || "Unlimited",
    playgroundRuns: subscription?.max_playground_runs_per_day || "Unlimited",
    requestLogs: subscription?.included_requests || "Unlimited",
    traces: "Unlimited",
  };
};
